import React from 'react';
import { Calendar, MapPin } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { exhibitions } from '../data/paintings';
import useScrollReveal from '../hooks/useScrollReveal';

const ExhibitionsPage = () => {
  const [headerRef, headerVisible] = useScrollReveal();
  const [currentRef, currentVisible] = useScrollReveal();
  const [pastRef, pastVisible] = useScrollReveal();
  const { t } = useTranslation();

  const upcomingExhibitions = exhibitions.filter(e => e.status === 'upcoming');
  const pastExhibitions = exhibitions.filter(e => e.status === 'past');

  return (
    <div className="min-h-screen bg-[#FAF7F2]">
      {/* Header */}
      <section ref={headerRef} className="pt-32 pb-20 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className={`text-center reveal ${headerVisible ? 'visible' : ''}`}>
            <span className="text-sm text-[#B8963E] tracking-widest uppercase mb-4 block">
              {t('exhibitions.label')}
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl mb-6 text-[#3D3831]">
              {t('exhibitions.title')}
            </h1>
            <div className="divider-elegant mb-6" />
            <p className="text-[#8B7E6A] max-w-2xl mx-auto text-lg">
              {t('exhibitions.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Upcoming Exhibitions */}
      <section ref={currentRef} className="py-16 px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <h2
            className={`text-2xl mb-10 text-[#3D3831] reveal ${currentVisible ? 'visible' : ''}`}
          >
            {t('exhibitions.upcoming')}
          </h2>

          <div className="space-y-8">
            {upcomingExhibitions.map((exhibition, index) => (
              <div
                key={exhibition.id}
                className={`bg-white p-8 shadow-lg border-l-4 border-[#B8963E] reveal reveal-delay-${
                  index + 1
                } ${currentVisible ? 'visible' : ''}`}
              >
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
                  <div className="flex-1">
                    <h3 className="text-2xl mb-4 text-[#3D3831]">
                      {exhibition.title}
                    </h3>

                    <div className="space-y-2 mb-4">
                      <div className="flex items-center text-[#5A5249]">
                        <Calendar size={18} className="mr-3 text-[#B8963E]" />
                        <span>{exhibition.date}</span>
                      </div>
                      <div className="flex items-center text-[#5A5249]">
                        <MapPin size={18} className="mr-3 text-[#B8963E]" />
                        <span>{exhibition.location}</span>
                      </div>
                    </div>

                    <p className="text-[#8B7E6A]">{exhibition.description}</p>
                  </div>

                  <div className="flex-shrink-0">
                    <button className="btn-gold whitespace-nowrap">
                      {t('exhibitions.details')}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className="max-w-5xl mx-auto px-6 lg:px-8">
        <div className="h-px bg-[#E8E2D9]" />
      </div>

      {/* Past Exhibitions */}
      <section ref={pastRef} className="py-16 px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <h2
            className={`text-2xl mb-10 text-[#3D3831] reveal ${pastVisible ? 'visible' : ''}`}
          >
            {t('exhibitions.past')}
          </h2>

          <div className="space-y-6">
            {pastExhibitions.map((exhibition, index) => (
              <div
                key={exhibition.id}
                className={`bg-white/50 p-6 border border-[#E8E2D9] reveal reveal-delay-${
                  index + 1
                } ${pastVisible ? 'visible' : ''}`}
              >
                <div className="flex flex-col md:flex-row md:items-center gap-4">
                  <div className="flex-1">
                    <h3 className="text-xl mb-2 text-[#3D3831]">
                      {exhibition.title}
                    </h3>
                    <p className="text-sm text-[#8B7E6A] mb-2">
                      {exhibition.date} · {exhibition.location}
                    </p>
                    <p className="text-[#5A5249]">{exhibition.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 lg:px-8 bg-[#3D3831]">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl mb-6 text-[#FAF7F2]">
            {t('exhibitions.organize.title')}
          </h2>
          <p className="text-[#C4B9A8] mb-8 leading-relaxed">
            {t('exhibitions.organize.description')}
          </p>
          <a
            href="mailto:info@irina-podlevskikh.ru"
            className="inline-block px-8 py-3.5 bg-[#B8963E] text-white hover:bg-[#9A7B32] transition-all duration-500 tracking-wider text-sm uppercase font-light"
          >
            {t('exhibitions.organize.contact')}
          </a>
        </div>
      </section>
    </div>
  );
};

export default ExhibitionsPage;
